// Предизборът на склад при разнасяне — чистата логика (без React): откъде
// тръгва работният лист, какъв белег стои до склада и кога има смисъл
// действието „Винаги от <склад>". Складът по съставка се решава на BE
// (`defaultAllocations`, правилото — в `api/inventoryProductWarehouses.ts`);
// до стар BE без полето всичко пада на единичния `defaultWarehouseId`.
import type { ReportPostingProduct } from "../../../../api/inventory";
import type { DefaultWarehouseSource } from "../../../../api/inventoryProductWarehouses";
import { QTY_EPSILON } from "./reportPostingShared";

export type DefaultShare = {
  warehouseId: number;
  quantity: number;
  source: DefaultWarehouseSource | null; // null — стар BE, източникът не е известен
};

// Дяловете по склад, както ги предлага BE: най-големият първи. Без
// `defaultAllocations` (стар BE) — целият продукт в `defaultWarehouseId`.
export const defaultSharesOf = (product: ReportPostingProduct): DefaultShare[] => {
  if (product.defaultAllocations)
    return product.defaultAllocations.map((allocation) => ({ ...allocation }));
  return product.defaultWarehouseId != null
    ? [{ warehouseId: product.defaultWarehouseId, quantity: product.requiredQuantity, source: null }]
    : [];
};

// Позициите, с които тръгва работният лист: всички с ЯВНО количество, освен
// последната — тя е балансьорът и поема остатъка. Балансьорът е НАЙ-ГОЛЕМИЯТ
// дял (BE го праща първи → тук отива последен): покритото от замяна намалява
// балансьора и при голям дял не го вкарва под нула.
export const initialAllocationsOf = (
  product: ReportPostingProduct,
): { warehouseId: number; quantity: number | null }[] => {
  const shares = defaultSharesOf(product);
  if (shares.length === 0) return [];
  const [largest, ...rest] = shares;
  return [
    ...rest.map((share) => ({ warehouseId: share.warehouseId, quantity: share.quantity })),
    { warehouseId: largest.warehouseId, quantity: null },
  ];
};

export type WarehouseHint =
  // складът е предложеният — белег откъде е дошъл
  | { kind: "source"; source: DefaultWarehouseSource }
  // операторът е сменил склада на ЕДИН друг — може да го направи „свой"
  | { kind: "pin"; warehouseId: number };

// Какво стои до склада на продукта. Без `defaultAllocations` (стар BE) —
// нищо: нито източникът е известен, нито „Склад по продукт" съществува.
export const warehouseHintOf = (
  product: ReportPostingProduct,
  currentWarehouseIds: number[],
): WarehouseHint | null => {
  if (!product.defaultAllocations || currentWarehouseIds.length === 0) return null;
  const defaultIds = product.defaultAllocations.map((allocation) => allocation.warehouseId);
  const sameAsDefault =
    defaultIds.length > 0 &&
    defaultIds.length === currentWarehouseIds.length &&
    defaultIds.every((warehouseId) => currentWarehouseIds.includes(warehouseId));
  if (sameAsDefault) return { kind: "source", source: product.defaultAllocations[0].source };
  // Разпределено на ръка между няколко склада — няма един склад за запомняне
  if (currentWarehouseIds.length === 1) return { kind: "pin", warehouseId: currentWarehouseIds[0] };
  return null;
};

// Продукт без склад: има какво да се изпише, но няма предизбор (нито свой склад
// на продукта, нито склад на станцията). Такъв продукт НЕ е „в недостиг" и НЕ е
// „наличен" — липсва настройка (станция без склад, без „Склад по продукт"),
// докато операторът не избере склад на ръка.
export const lacksDefaultWarehouse = (product: ReportPostingProduct): boolean =>
  product.requiredQuantity > QTY_EPSILON && defaultSharesOf(product).length === 0;

// Недостиг по предизбора (за статуса на чакащ отчет в списъка): дял над
// наличното в своя склад. Без предизбор недостиг няма как да се каже — складът
// още не е избран (`lacksDefaultWarehouse`), затова такъв продукт не се брои тук.
export const isShortByDefaults = (product: ReportPostingProduct): boolean => {
  const availableIn = (warehouseId: number) =>
    Math.max(
      0,
      product.warehouses.find((option) => option.warehouseId === warehouseId)?.available ?? 0,
    );
  return defaultSharesOf(product).some(
    (share) => share.quantity > availableIn(share.warehouseId) + QTY_EPSILON,
  );
};
